import { useState } from 'react';
import { useRecordCompletionSource, type UnsourcedRule } from '../../api/triage.ts';
import { field, label } from '../contacts/Sheet.tsx';
import { Refusal, go, plain } from './Fields.tsx';

/**
 * On Triage: the rules with no completion source, whose deadlines from before each client entered Procura are
 * withheld (146), and, for tiers 1-2 only, "This rule can now be recorded" (P-31, A-#286 Q4 A) on a rule a record can
 * show done (the oath and designation since 154: the order's oath date). The act is 146's set_rule_completion_source(), with a
 * REQUIRED reason, recorded with who and when; it releases nothing by itself: the rule's withheld deadlines then count
 * as held or triage, like any other. A rule no record shows done is listed with no button. Hidden when there are none.
 * Its own file, because Triage.tsx keeps no state of its own (P-30).
 */
export function CompletionSources({ rules }: { rules: readonly UnsourcedRule[] }) {
  if (rules.length === 0) return null;
  return (
    <section aria-labelledby="sources" className="mt-5">
      <h2 id="sources" className="text-sm font-bold tracking-wide text-neutral-700 uppercase">Rules with no completion source</h2>
      <ul className="mt-2 divide-y divide-neutral-200 rounded-lg border border-neutral-300">
        {rules.map((r) => <SourceRule key={r.rule_code} rule={r} />)}
      </ul>
    </section>
  );
}

function SourceRule({ rule }: { rule: UnsourcedRule }) {
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState('');
  const act = useRecordCompletionSource();
  return (
    <li data-source-rule={rule.rule_code} className="px-3 py-2">
      <p className="font-semibold text-neutral-900">{rule.label}</p>
      <p className="text-sm text-neutral-700">
        {rule.record_kind ? 'Its deadlines from before each client entered Procura are withheld until this is recorded.'
          : 'No record shows this rule done, so its deadlines from before each client entered Procura stay withheld.'}
      </p>
      {act.data ? (
        <p role="status" className="mt-1 text-sm text-neutral-800">Recorded. Its withheld deadlines are now held or in triage.</p>
      ) : rule.may_record_source && !open ? (
        <button type="button" className={`${plain} mt-1`} onClick={() => { setOpen(true); setWhy(''); act.reset(); }}>
          This rule can now be recorded
        </button>
      ) : rule.may_record_source && open ? (
        <form className="mt-2 space-y-2" onSubmit={(e) => {
          e.preventDefault();
          if (!why.trim() || act.isPending) return;
          act.mutate({ rule_code: rule.rule_code, reason: why.trim() });
        }}>
          <label className={label}>Why it can now be recorded (required)
            <input type="text" required maxLength={2000} value={why} onChange={(e) => setWhy(e.target.value)} className={field} />
          </label>
          <Refusal error={act.error} />
          <div className="flex flex-wrap gap-2">
            <button type="submit" className={go} disabled={!why.trim() || act.isPending}>Record it</button>
            <button type="button" className={plain} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      ) : null}
    </li>
  );
}
